import React, { useState } from 'react';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, {
	CardBody,
	CardFooter,
	CardFooterLeft,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import useDarkMode from '../../../hooks/useDarkMode';
import { demoPagesMenu } from '../../../menu';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import Breadcrumb from '../../../components/bootstrap/Breadcrumb';
import Accordion, { AccordionItem } from '../../../components/bootstrap/Accordion';
import Icon from '../../../components/icon/Icon';
import { toast } from 'react-toastify';
import { useChangePassword } from '../../../apis/hooks/auth';

interface ChangePasswordFormValues {
	validationUserName: string;
	validationOldPassword: string;
	validationNewPassword: string;
	validationConfirmPassword: string;
}

const ChangePassword = () => {
	const { darkModeStatus } = useDarkMode();

	const [oldPassIsVisible, setOldPassVisible] = useState<boolean>(false);
	const [newPassIsVisible, setNewPassVisible] = useState<boolean>(false);
	const [confirmPassIsVisible, setconfirmPassVisible] = useState<boolean>(false);

	const oldPassToggle = () => {
		setOldPassVisible(!oldPassIsVisible);
	};
	const newPassToggle = () => {
		setNewPassVisible(!newPassIsVisible);
	};
	const confirmPassToggle = () => {
		setconfirmPassVisible(!confirmPassIsVisible);
	};

	const { mutate: changePassword } = useChangePassword();

	const formik = useFormik<ChangePasswordFormValues>({
		initialValues: {
			validationUserName: localStorage.getItem('username') ?? '',
			validationOldPassword: '',
			validationNewPassword: '',
			validationConfirmPassword: '',
		},
		validate: (values) => {
			const errors: Partial<Record<keyof ChangePasswordFormValues, string>> = {};
			// Validation logic:
			if (!values.validationUserName) {
				// Show "Required" error if field is empty and untouched
				errors.validationUserName = 'Required';
			} else if (!values.validationUserName.trim()) {
				// Show "Name cannot be just spaces" if field contains only spaces
				errors.validationUserName = 'Name cannot be just spaces';
			}
			return errors;
		},
		onSubmit: (values) => {
			if (values.validationNewPassword !== values.validationConfirmPassword) {
				toast.warn('New & Confirm Passwords are not matched');
				return; // Prevent submission
			}
			const payload = {
				username: values.validationUserName,
				old_password: values.validationOldPassword,
				new_password: values.validationNewPassword,
				confirm_password: values.validationConfirmPassword,
			};

			changePassword(payload, {
				onSuccess: (res: any) => {
					if (res?.status) {
						toast.success(res?.message, {
							closeOnClick: true,
							closeButton: true,
						});
					} else {
						toast.error(res?.message);
					}
				},
			});
		},
	});

	return (
		<PageWrapper title={demoPagesMenu.changePassword.text}>
			<SubHeader>
				<SubHeaderLeft>
					<Breadcrumb
						list={[
							{ title: 'Change Password', to: '/company' },
							{
								title: 'Change Password',
								to: '#',
							},
						]}
					/>
				</SubHeaderLeft>
				<SubHeaderRight>
					<SubheaderSeparator />
					<Button icon='ArrowBackIosNew' color='primary' isLight tag='a' to='/'>
						Go Back
					</Button>
				</SubHeaderRight>
			</SubHeader>

			<Page container='fluid'>
				<Card tag='form' noValidate onSubmit={formik.handleSubmit}>
					<CardHeader>
						<CardLabel>
							<CardTitle>{'Change Password'}</CardTitle>
						</CardLabel>
					</CardHeader>

					<CardBody>
						{/* Login Info */}
						<Accordion
							id='accSample-shadow-1'
							shadow='default'
							className='mb-5'
							activeItemId='accor1-shadow-1'>
							<AccordionItem id='accor1-shadow-1' title='Login Info' icon='login'>
								<div className='row g-4'>
									<FormGroup
										id='validationUserName'
										label='Email'
										className='col-md-6'>
										<Input {...formik.getFieldProps('validationUserName')} />
										{formik.errors.validationUserName ? (
											<div className='text-danger'>
												{formik.errors.validationUserName}
											</div>
										) : (
											<></>
										)}
									</FormGroup>
									<FormGroup
										id='validationOldPassword'
										label='Old Password'
										className='col-md-6'>
										<div className='input-group'>
											<Input
												type={!oldPassIsVisible ? 'password' : 'text'}
												className='form-control'
												placeholder='Enter Old Password'
												name='validationOldPassword'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.validationOldPassword}
											/>
											<span
												className='input-group-text'
												style={{ cursor: 'pointer' }}
												onClick={oldPassToggle}>
												{oldPassIsVisible ? (
													<Icon icon='Visibility' />
												) : (
													<Icon icon='VisibilityOff' />
												)}
											</span>
										</div>
									</FormGroup>
									<FormGroup
										id='validationNewPassword'
										label='New Password'
										className='col-md-6'>
										<div className='input-group'>
											<Input
												type={!newPassIsVisible ? 'password' : 'text'}
												className='form-control'
												placeholder='Enter New Password'
												name='validationNewPassword'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.validationNewPassword}
											/>
											<span
												className='input-group-text'
												style={{ cursor: 'pointer' }}
												onClick={newPassToggle}>
												{newPassIsVisible ? (
													<Icon icon='Visibility' />
												) : (
													<Icon icon='VisibilityOff' />
												)}
											</span>
										</div>
									</FormGroup>
									<FormGroup
										id='validationConfirmPassword'
										label='Confirm Password'
										className='col-md-6'>
										<div className='input-group'>
											<Input
												type={!confirmPassIsVisible ? 'password' : 'text'}
												className='form-control'
												placeholder='Enter Confirm Password'
												name='validationConfirmPassword'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.validationConfirmPassword}
											/>
											<span
												className='input-group-text'
												style={{ cursor: 'pointer' }}
												onClick={confirmPassToggle}>
												{confirmPassIsVisible ? (
													<Icon icon='Visibility' />
												) : (
													<Icon icon='VisibilityOff' />
												)}
											</span>
										</div>
									</FormGroup>
								</div>
							</AccordionItem>
						</Accordion>
					</CardBody>

					<CardFooter style={{ flexDirection: 'row' }}>
						<CardFooterLeft>
							<Button type='submit' color='primary' className='fs-5'>
								{'Update'}
							</Button>
						</CardFooterLeft>
						<CardFooterRight>
							<Button
								type='reset'
								color='info'
								isOutline
								onClick={formik.handleReset}
								className='fs-5'>
								Reset
							</Button>
						</CardFooterRight>
					</CardFooter>
				</Card>
			</Page>
		</PageWrapper>
	);
};

export default ChangePassword;
